import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '../common/Catalyst/dialog';
import { Button } from '../common/Catalyst/button';
import { AmenityGroupReturn, AmenityReturn } from '../clients/rust-api-client';
import { amenityGroupService } from '../clients/ApiClients';
import { optionalInt, reservationErrorMessage } from '../reservation/reservationMeta';
import { groupLimitsSummary } from '../reservation/phaseThreeMeta';
import {
  checkboxClass,
  helpClass,
  inputClass,
  labelClass,
  sectionTitleClass,
} from '../reservation/formStyles';
import { ErrorAlert } from '../common/Custom/ErrorAlert';

/**
 * Amenity groups (#642, phase 3): interchangeable amenities — two tennis
 * courts, three party rooms — and the limits a household shares across
 * them. Households can book "any court" and get the first one free.
 *
 * Membership is the whole list on every save; info-only amenities cannot
 * join, and an amenity is in at most one group.
 */
interface Props {
  hoaId: string;
  amenities: AmenityReturn[];
  readOnly: boolean;
  /** A group changed: the amenities' `group_name` did too. */
  onChanged: () => void;
}

export const AmenityGroupsSection: React.FC<Props> = ({ hoaId, amenities, readOnly, onChanged }) => {
  const [groups, setGroups] = useState<AmenityGroupReturn[]>([]);
  const [editing, setEditing] = useState<AmenityGroupReturn | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setGroups((await amenityGroupService.listAmenityGroups({ hoaId })).data.groups);
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't load the amenity groups."));
    }
  }, [hoaId]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (g: AmenityGroupReturn) => {
    if (!window.confirm(`Delete the group “${g.name}”? Its amenities stay; the shared limits go.`)) return;
    try {
      await amenityGroupService.deleteAmenityGroup({ groupId: g.id });
      await load();
      onChanged();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't delete the group."));
    }
  };

  if (readOnly && groups.length === 0) return null;

  return (
    <section id="groups" className="scroll-mt-6 space-y-3" aria-labelledby="amenity-groups-title">
      <div className="flex items-center gap-3">
        <h2 id="amenity-groups-title" className={clsx(sectionTitleClass, 'mr-auto')}>
          Amenity groups
        </h2>
        {!readOnly && (
          <Button outline onClick={() => setEditing('new')}>
            New group
          </Button>
        )}
      </div>
      <p className={helpClass}>
        Group interchangeable amenities — several courts, several rooms — so households can book “any court”
        and so per-household limits count across all of them.
      </p>
      <ErrorAlert>{error}</ErrorAlert>
      {groups.length === 0 ? (
        <p className="text-[13px] text-fg-muted">No groups.</p>
      ) : (
        <ul className="divide-y divide-line/60 rounded-lg border border-line">
          {groups.map((g) => (
            <li key={g.id} className="flex items-center gap-3 px-3 py-2 text-[13px]">
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-fg">{g.name}</span>
                <span className="block text-[12px] text-fg-muted">
                  {[...g.amenities]
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((m) => m.name)
                    .join(', ') || 'No amenities'}
                  {' · '}
                  {groupLimitsSummary(g)}
                </span>
              </span>
              {!readOnly && (
                <>
                  <Button plain onClick={() => setEditing(g)}>
                    Edit
                  </Button>
                  <Button plain onClick={() => remove(g)}>
                    Delete
                  </Button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {!readOnly && (
        <GroupDialog
          hoaId={hoaId}
          group={editing}
          amenities={amenities}
          groups={groups}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
            onChanged();
          }}
        />
      )}
    </section>
  );
};

const GroupDialog: React.FC<{
  hoaId: string;
  group: AmenityGroupReturn | 'new' | null;
  amenities: AmenityReturn[];
  groups: AmenityGroupReturn[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ hoaId, group, amenities, groups, onClose, onSaved }) => {
  const existing = group && group !== 'new' ? group : null;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [members, setMembers] = useState<string[]>([]);
  const [daily, setDaily] = useState('');
  const [weekly, setWeekly] = useState('');
  const [monthly, setMonthly] = useState('');
  const [active, setActive] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!group) return;
    const g = group === 'new' ? null : group;
    setName(g?.name ?? '');
    setDescription(g?.description ?? '');
    setMembers(g?.amenities.map((m) => m.id) ?? []);
    const s = (n?: number | null) => (n === null || n === undefined ? '' : String(n));
    setDaily(s(g?.per_home_daily_limit));
    setWeekly(s(g?.per_home_weekly_limit));
    setMonthly(s(g?.per_home_monthly_limit));
    setActive(s(g?.per_home_active_limit));
    setError(null);
  }, [group]);

  // Bookable amenities that are not in another group.
  const otherGroups = new Set(
    groups.filter((g) => g.id !== existing?.id).flatMap((g) => g.amenities.map((m) => m.id))
  );
  const candidates = amenities.filter(
    (a) => a.booking_mode !== 'info_only' && !a.archived_at && !otherGroups.has(a.id)
  );
  const limits = {
    daily: optionalInt(daily),
    weekly: optionalInt(weekly),
    monthly: optionalInt(monthly),
    active: optionalInt(active),
  };
  const valid = name.trim().length > 0 && !Object.values(limits).some((v) => Number.isNaN(v));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    setError(null);
    const body = {
      name: name.trim(),
      description: description.trim() || null,
      amenity_ids: members,
      per_home_daily_limit: limits.daily,
      per_home_weekly_limit: limits.weekly,
      per_home_monthly_limit: limits.monthly,
      per_home_active_limit: limits.active,
    };
    try {
      if (existing) {
        await amenityGroupService.updateAmenityGroup({ groupId: existing.id, amenityGroupUpdate: body });
      } else {
        await amenityGroupService.createAmenityGroup({ amenityGroupCreate: { ...body, hoa_id: hoaId } });
      }
      onSaved();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't save the group."));
    } finally {
      setSaving(false);
    }
  };

  const limitInput = (label: string, value: string, set: (v: string) => void) => (
    <label className={labelClass}>
      {label}
      <input
        inputMode="numeric"
        value={value}
        onChange={(e) => set(e.target.value.replace(/\D/g, ''))}
        placeholder="No limit"
        className={clsx(inputClass, 'w-28')}
      />
    </label>
  );

  return (
    <Dialog open={group !== null} onClose={onClose} size="lg">
      <form onSubmit={submit}>
        <DialogTitle>{existing ? `Edit ${existing.name}` : 'New amenity group'}</DialogTitle>
        <DialogDescription>Interchangeable amenities and the limits a household shares across them.</DialogDescription>
        <DialogBody className="space-y-4">
          <label className={labelClass}>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tennis courts" className={inputClass} />
          </label>
          <label className={labelClass}>
            Description
            <input value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass} />
          </label>
          <fieldset className="space-y-1">
            <legend className="mb-1 text-[12px] font-medium text-fg-muted">Amenities (booked in this order)</legend>
            {candidates.length === 0 ? (
              <p className={helpClass}>No bookable amenities are free to add.</p>
            ) : (
              candidates
                .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))
                .map((a) => (
                  <label key={a.id} className="flex items-center gap-2 text-[13px] text-fg-secondary">
                    <input
                      type="checkbox"
                      className={checkboxClass}
                      checked={members.includes(a.id)}
                      onChange={(e) =>
                        setMembers((m) => (e.target.checked ? [...m, a.id] : m.filter((id) => id !== a.id)))
                      }
                    />
                    {a.name}
                  </label>
                ))
            )}
          </fieldset>
          <div>
            <p className="mb-1 text-[12px] font-medium text-fg-muted">Per household, across the whole group</p>
            <div className="flex flex-wrap gap-3">
              {limitInput('Per day', daily, setDaily)}
              {limitInput('Per week', weekly, setWeekly)}
              {limitInput('Per month', monthly, setMonthly)}
              {limitInput('Upcoming at once', active, setActive)}
            </div>
            <p className={clsx(helpClass, 'mt-1')}>Counted beside each amenity’s own limits.</p>
          </div>
          <ErrorAlert>{error}</ErrorAlert>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button color="sky" type="submit" disabled={!valid || saving}>
            {saving ? 'Saving…' : existing ? 'Save group' : 'Create group'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AmenityGroupsSection;
